import type { IntegrationId } from '../tabularMappings/types';
import {
  type APIWorkspaceAttributes,
  type CloudSchedule,
  type IntegrationSchedule,
  ScheduleType,
  type ServiceNowSchedule,
  type UnifiedSchedule,
  type UnifiedScheduleType,
} from '@ardoq/api-types';
import {
  type CelonisExportSchedule,
  CLOUD_PROVIDER_TYPE,
  type Connection,
  type IntegrationWorkspace,
  type ITPediaSchedule,
  type SignavioExportSchedule,
} from '@ardoq/api-types/integrations';
import {
  INTEGRATION_ID_TO_SCHEDULE_TYPE,
  UNIFIED_INTEGRATION_IDS,
  UNIFIED_SCHEDULE_TYPES,
} from 'integrations/unified/constants';
import { createUnifiedIntegrationRecord } from 'integrations/unified/utils';
import { currentDate, isSameDay, toZonedTime } from '@ardoq/date-time';
import type { UnifiedIntegrationId } from 'integrations/unified/types';
import { isJobWithTabularMapping } from 'integrations/common/utils/scheduleApi';

// We run our schedules at UTC+0
// https://ardoqcom.atlassian.net/browse/ARD-24820
export const isScheduleWorkedTodayInUTC = (date: Date) =>
  isSameDay(toZonedTime(currentDate(), 'UTC'), toZonedTime(date, 'UTC'));

export const getScheduleDirectionId = (
  type: ScheduleType
): 'import' | 'export' => {
  return type.endsWith('-export') ? 'export' : 'import';
};

export const getAffectedSchedules = (
  schedules: IntegrationSchedule[],
  connectionId: string
) =>
  schedules.filter(
    schedule =>
      ('servicenowConfig' in schedule.jobOptions &&
        schedule.jobOptions.servicenowConfig.connectionId === connectionId) ||
      ('cloudProviderConfig' in schedule.jobOptions &&
        schedule.jobOptions.cloudProviderConfig.accountIds.includes(
          connectionId
        )) ||
      ('sourceConfig' in schedule.jobOptions &&
        schedule.jobOptions.sourceConfig.connectionId === connectionId)
  );

export const UNIFIED_INTEGRATIONS_WITHOUT_SCHEDULES: Array<UnifiedIntegrationId> =
  ['ardoq-sample-data'];

const INTEGRATION_IDS_WITH_SCHEDULES: Array<IntegrationId> = [
  'aws-v3',
  'azure-v3',
  'servicenow-v3',
  'signavio-exporter',
  'it-pedia',
  'celonis-exporter',
  ...UNIFIED_INTEGRATION_IDS.filter(
    id => !UNIFIED_INTEGRATIONS_WITHOUT_SCHEDULES.includes(id)
  ),
];

export const isIntegrationWithSchedules = (integrationId: IntegrationId) =>
  INTEGRATION_IDS_WITH_SCHEDULES.includes(integrationId);

export const isCloudSchedule = (
  job?: IntegrationSchedule
): job is CloudSchedule =>
  job ? job.jobOptions.type === ScheduleType.CLOUD_IMPORT : false;

export const isUnifiedSchedule = (
  job?: IntegrationSchedule
): job is UnifiedSchedule =>
  job ? UNIFIED_SCHEDULE_TYPES.includes(job.jobOptions.type) : false;

export const isUnifiedScheduleFor = (
  unifiedScheduleType: UnifiedScheduleType,
  job?: IntegrationSchedule
): job is UnifiedSchedule =>
  isUnifiedSchedule(job) && job.jobOptions.type === unifiedScheduleType;

export const isServiceNowSchedule = (
  job?: IntegrationSchedule
): job is ServiceNowSchedule =>
  job
    ? [ScheduleType.SERVICENOW_EXPORT, ScheduleType.SERVICENOW_IMPORT].includes(
        job.jobOptions.type
      )
    : false;

export const isSignavioExportSchedule = (
  job?: IntegrationSchedule
): job is SignavioExportSchedule => {
  return job?.jobOptions?.type === ScheduleType.SIGNAVIO_EXPORT;
};

export const isCelonisExportSchedule = (
  job?: IntegrationSchedule
): job is CelonisExportSchedule => {
  return job?.jobOptions?.type === ScheduleType.CELONIS_EXPORT;
};

export const isITPediaSchedule = (
  job?: IntegrationSchedule
): job is ITPediaSchedule => {
  return job?.jobOptions?.type === ScheduleType.ITPEDIA_ENRICHMENT;
};

const isCloudProviderSchedule = (
  cloudProvider: CLOUD_PROVIDER_TYPE,
  job?: IntegrationSchedule
): job is CloudSchedule =>
  job
    ? isCloudSchedule(job) && job.jobOptions.provider === cloudProvider
    : false;

const integrationToIsIntegrationSchedule: Record<
  IntegrationId,
  (job?: IntegrationSchedule) => boolean
> = {
  'excel-v3': () => false,
  'json-importer': () => false,
  'aws-v3': job => isCloudProviderSchedule(CLOUD_PROVIDER_TYPE.AWS, job),
  'azure-v3': job => isCloudProviderSchedule(CLOUD_PROVIDER_TYPE.AZURE, job),
  'servicenow-v3': isServiceNowSchedule,
  'signavio-exporter': isSignavioExportSchedule,
  'it-pedia': isITPediaSchedule,
  'celonis-exporter': isCelonisExportSchedule,
  'servicenow-guided-exporter': () => false,
  ...createUnifiedIntegrationRecord(
    integrationId => job =>
      isUnifiedScheduleFor(INTEGRATION_ID_TO_SCHEDULE_TYPE[integrationId], job)
  ),
};

export const filterSchedulesByIntegrationId = (
  schedules: IntegrationSchedule[],
  integrationId: IntegrationId
) =>
  schedules.filter(job =>
    integrationToIsIntegrationSchedule[integrationId](job)
  );

export const getIntegrationIdForSchedule = (
  schedule: IntegrationSchedule
): IntegrationId | null => {
  const integrationId = (
    Object.keys(integrationToIsIntegrationSchedule) as IntegrationId[]
  ).find(id => integrationToIsIntegrationSchedule[id](schedule));

  return integrationId ?? null;
};

export const getScheduleConnectionName = (
  job: IntegrationSchedule,
  connections: Connection[]
) => {
  if (isServiceNowSchedule(job)) {
    return (
      connections.find(
        c => c._id === job.jobOptions.servicenowConfig.connectionId
      )?.name || null
    );
  }

  if (isCloudSchedule(job)) {
    return connections
      .filter(({ _id }) =>
        job.jobOptions.cloudProviderConfig.accountIds.includes(_id)
      )
      .map(({ name }) => name)
      .join(', ');
  }

  if (isUnifiedSchedule(job)) {
    return (
      connections.find(c => c._id === job.jobOptions.sourceConfig.connectionId)
        ?.name || null
    );
  }

  if (isSignavioExportSchedule(job)) {
    return (
      connections.find(
        connection => connection._id === job.jobOptions.accountId
      )?.name || null
    );
  }

  if (isITPediaSchedule(job)) {
    return (
      connections.find(
        connection => connection._id === job.jobOptions.accountId
      )?.name || null
    );
  }

  return null;
};

export const getScheduleSourceConfig = (schedule: IntegrationSchedule) => {
  if (isUnifiedSchedule(schedule)) {
    return schedule.jobOptions.sourceConfig;
  }
  if (isServiceNowSchedule(schedule)) {
    return schedule.jobOptions.servicenowConfig;
  }
  if (isCloudSchedule(schedule)) {
    return schedule.jobOptions.cloudProviderConfig;
  }

  return {};
};

export const isJobOptionsWithWorkspace = (
  jobOptions: IntegrationSchedule['jobOptions']
): jobOptions is IntegrationSchedule['jobOptions'] & {
  workspace: IntegrationWorkspace;
} => {
  return 'workspace' in jobOptions && Boolean(jobOptions.workspace?.id);
};

const updateWorkspaceName = (
  workspace: IntegrationWorkspace,
  workspaces: Record<string, APIWorkspaceAttributes>
) => {
  if (!workspace.id || !workspaces[workspace.id]) return workspace;
  return {
    id: workspace.id,
    name: workspaces[workspace.id].name,
  };
};

export const applyScheduleWorkspaceNameUpdate = (
  originalSchedule: IntegrationSchedule,
  workspaces: Record<string, APIWorkspaceAttributes>
): IntegrationSchedule => {
  const schedule = structuredClone(originalSchedule);
  if (isJobOptionsWithWorkspace(schedule.jobOptions)) {
    if (!schedule.jobOptions.workspace.id) return schedule;
    schedule.jobOptions.workspace = updateWorkspaceName(
      schedule.jobOptions.workspace,
      workspaces
    );
  }
  if (isJobWithTabularMapping(schedule.jobOptions)) {
    schedule.jobOptions.tabularMapping.tables =
      schedule.jobOptions.tabularMapping.tables.map(table => ({
        ...table,
        rootWorkspace: updateWorkspaceName(table.rootWorkspace, workspaces),
        targetWorkspace: table.targetWorkspace
          ? updateWorkspaceName(table.targetWorkspace, workspaces)
          : table.targetWorkspace,
      }));
  }
  if (isITPediaSchedule(schedule)) {
    schedule.jobOptions.workspaces.products = updateWorkspaceName(
      schedule.jobOptions.workspaces.products,
      workspaces
    );
    if (schedule.jobOptions.workspaces.vulnerabilities)
      schedule.jobOptions.workspaces.vulnerabilities = updateWorkspaceName(
        schedule.jobOptions.workspaces.vulnerabilities,
        workspaces
      );
  }
  return schedule;
};
